// src/services/api.js
// Central API service — all calls to the ShipDoTrack backend

export const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Token management ──────────────────────────────────────────────────────────
export function getToken() {
  return localStorage.getItem('ft_token');
}
export function setToken(token) {
  localStorage.setItem('ft_token', token);
}
export function removeToken() {
  localStorage.removeItem('ft_token');
}

// ── Core fetch wrapper ────────────────────────────────────────────────────────
async function request(method, path, body = null) {
  const token = getToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const options = { method, headers };
  if (body && method !== 'GET') options.body = JSON.stringify(body);

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, options);
  } catch {
    // fetch() only rejects when no real response came back at all — server
    // down/restarting, a proxy answering 503 without CORS headers (which the
    // browser reports as a CORS error), or a dropped connection. Callers can
    // tell this apart from a normal HTTP error because it has no `status`.
    throw new Error('Cannot reach the server right now (it may be restarting or overloaded). Try again in a moment.');
  }

  // Token expired
  if (res.status === 401) {
    removeToken();
    window.location.reload();
    return;
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return data;
}

const get    = (path)        => request('GET',    path);
const post   = (path, body)  => request('POST',   path, body);
const put    = (path, body)  => request('PUT',    path, body);
const del    = (path)        => request('DELETE', path);

// ── Auth ──────────────────────────────────────────────────────────────────────
export const authAPI = {
  login:          (email, password) => post('/auth/login', { email, password }),
  me:             ()                => get('/auth/me'),
  changePassword: (currentPassword, newPassword) => post('/auth/change-password', { currentPassword, newPassword }),
};

// ── Dashboard ─────────────────────────────────────────────────────────────────
export const dashboardAPI = {
  stats: () => get('/dashboard/stats'),
};

// ── Users ─────────────────────────────────────────────────────────────────────
export const usersAPI = {
  list:   ()           => get('/users'),
  create: (data)       => post('/users', data),
  update: (id, data)   => put(`/users/${id}`, data),
  remove: (id)         => del(`/users/${id}`),
};

// ── Clients ───────────────────────────────────────────────────────────────────
export const clientsAPI = {
  list:   ()           => get('/clients'),
  create: (data)       => post('/clients', data),
  update: (id, data)   => put(`/clients/${id}`, data),
  remove: (id)         => del(`/clients/${id}`),
};

// ── GPS Devices ───────────────────────────────────────────────────────────────
export const gpsAPI = {
  list:   ()           => get('/gps'),
  create: (data)       => post('/gps', data),
  update: (id, data)   => put(`/gps/${id}`, data),
  remove: (id)         => del(`/gps/${id}`),
};

// ── Trucks ────────────────────────────────────────────────────────────────────
export const trucksAPI = {
  list:   ()           => get('/trucks'),
  create: (data)       => post('/trucks', data),
  update: (id, data)   => put(`/trucks/${id}`, data),
  remove: (id)         => del(`/trucks/${id}`),
};

// ── Documents ─────────────────────────────────────────────────────────────────
export const documentsAPI = {
  list:   ()           => get('/documents'),
  create: (data)       => post('/documents', data),
  update: (id, data)   => put(`/documents/${id}`, data),
  remove: (id)         => del(`/documents/${id}`),
};

// ── Geofences ─────────────────────────────────────────────────────────────────
export const geofencesAPI = {
  list:   ()           => get('/geofences'),
  create: (data)       => post('/geofences', data),
  update: (id, data)   => put(`/geofences/${id}`, data),
  remove: (id)         => del(`/geofences/${id}`),
};

// ── Travel History ────────────────────────────────────────────────────────────
export const travelHistoryAPI = {
  list:            ()         => get('/travel-history'),
  // One truck's history (not capped by other trucks' rows). `limit` keeps the
  // frequent refresh cheap — it only needs the newest few rows.
  listForTruck:    (truckId, limit) => get(`/travel-history?truckId=${encodeURIComponent(truckId)}${limit ? `&limit=${limit}` : ''}`),
  create:          (data)     => post('/travel-history', data),
  bulkCreate:      (entries)  => post('/travel-history/bulk', { entries }),
  removeForTruck:  (truckId)  => del(`/travel-history/truck/${truckId}`),
};

// ── App Settings (shared, not per-browser) ─────────────────────────────────────
export const settingsAPI = {
  get:    ()      => get('/settings'),
  update: (data)  => put('/settings', data),
};

// ── GPS SIM Load Records ─────────────────────────────────────────────────────
export const gpsLoadsAPI = {
  list:   ()          => get('/gps-loads'),
  create: (data)       => post('/gps-loads', data),
  update: (id, data)   => put(`/gps-loads/${id}`, data),
  remove: (id)         => del(`/gps-loads/${id}`),
};

// ── Saved Locations ───────────────────────────────────────────────────────────
export const savedLocationsAPI = {
  list:   ()      => get('/saved-locations'),
  create: (data)  => post('/saved-locations', data),
  remove: (id)    => del(`/saved-locations/${id}`),
};
